// src/modules/accounting/pages/PostingRulesPage.jsx
//
// Accounting → Posting Rules. Shows every automatic journal entry the system
// posts, broken into its debit/credit legs, and lets an admin re-point each leg
// at a different Chart-of-Accounts account.
//
// Two deliberate constraints, mirrored from the backend:
//   • the AMOUNT of each leg is read-only — it is computed in code, so a config
//     change can never unbalance an entry. It is shown as descriptive text.
//   • nothing saves directly. Edits collect in a local buffer and are submitted
//     as one approval request (POSTING_RULE_APPROVAL).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, Button, Card, Col, Collapse, Empty, Input, Modal, Row,
  Segmented, Select, Space, Spin, Tag, Tooltip, Typography, message
} from 'antd';
import {
  ReloadOutlined, SaveOutlined, UndoOutlined, ThunderboltOutlined,
  HistoryOutlined, StopOutlined, InfoCircleOutlined, WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getPostingRules, seedPostingRules, createPostingRuleChange,
  listPostingRuleChanges, cancelPostingRuleChange, getPostingRuleHistory,
  getAccounts,
} from '../api';
import { NATURE_MAP } from '../constants';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text, Paragraph } = Typography;

const SIDE_TAG = {
  DEBIT: <Tag color="blue">Dr</Tag>,
  CREDIT: <Tag color="volcano">Cr</Tag>,
  EITHER: <Tag>Dr/Cr</Tag>,
};

const reqStatusColors = {
  PENDING: 'gold', APPROVED: 'green', REJECTED: 'red',
  CANCELLED: 'default', FAILED: 'volcano',
};

const apiError = (err) =>
  err?.response?.data?.message || err.message || 'Operation failed';

/** A leg's current target, or the reason it has none. */
function AccountBadge({ current }) {
  if (!current) return <Text type="secondary">—</Text>;
  if (!current.ok) {
    return (
      <Tooltip title="This account does not exist or is disabled. Entries fall back to the built-in default; run the Chart of Accounts bootstrap to fix it.">
        <Tag color="red" icon={<WarningOutlined />}>{current.code || 'unresolved'}</Tag>
      </Tooltip>
    );
  }
  const label = `${current.code} — ${current.name}`;
  if (current.source === 'FALLBACK') {
    return (
      <Tooltip title="This rule was configured, but the account it pointed at is gone or disabled — the built-in default is being used instead.">
        <Tag color="orange">{label}</Tag>
      </Tooltip>
    );
  }
  return <Text>{label}</Text>;
}

export default function PostingRulesPage() {
  const [data, setData] = useState({ groups: [], meta: {} });
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ?view=requests deep-links here from the approval queue's View button.
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get('view') === 'requests' ? 'requests' : 'rules');

  const changeView = (next) => {
    setView(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'requests') params.set('view', 'requests');
    else params.delete('view');
    setSearchParams(params, { replace: true });
  };

  // ruleKey → { accountId } | { variants: { [value]: accountId } }
  const [pending, setPending] = useState({});
  const [submitOpen, setSubmitOpen] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const [requests, setRequests] = useState({ data: [], meta: {} });
  const [reqLoading, setReqLoading] = useState(false);
  const [historyModal, setHistoryModal] = useState(null);

  /* ── Load ── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rules, accts] = await Promise.all([
        getPostingRules(),
        getAccounts({ limit: 500 }),
      ]);
      setData({ groups: rules.groups || [], meta: rules.meta || {} });
      const list = accts.data || accts || [];
      setAccounts(Array.isArray(list) ? list.filter((a) => a.isActive) : []);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchRequests = useCallback(async () => {
    setReqLoading(true);
    try {
      const res = await listPostingRuleChanges({ page: 1, limit: 50 });
      setRequests({ data: res.data || [], meta: res.meta || {} });
    } catch (err) {
      message.error(apiError(err));
    } finally {
      setReqLoading(false);
    }
  }, []);

  useEffect(() => { if (view === 'requests') fetchRequests(); }, [view, fetchRequests]);

  /* ── Account picker options, grouped so a novice picks a sane account ── */
  const accountOptions = useMemo(() => {
    const byStatement = { 'Balance Sheet': [], 'Profit & Loss': [], Unclassified: [] };
    accounts.forEach((a) => {
      const stmt = NATURE_MAP[a.nature]?.statement || 'Unclassified';
      byStatement[stmt].push({
        value: a._id,
        label: `${a.code} — ${a.name}`,
        title: NATURE_MAP[a.nature]?.label || 'Unclassified',
      });
    });
    return Object.entries(byStatement)
      .filter(([, opts]) => opts.length)
      .map(([label, options]) => ({ label, options }));
  }, [accounts]);

  /* ── Edit buffer ── */
  const setDefault = (ruleKey, accountId) => {
    setPending((prev) => ({ ...prev, [ruleKey]: { ...prev[ruleKey], accountId } }));
  };

  const setVariant = (ruleKey, value, accountId) => {
    setPending((prev) => ({
      ...prev,
      [ruleKey]: {
        ...prev[ruleKey],
        variants: { ...(prev[ruleKey]?.variants || {}), [value]: accountId },
      },
    }));
  };

  const discard = () => { setPending({}); message.info('Pending edits discarded'); };

  const pendingCount = Object.keys(pending).length;

  /* ── Build the payload the backend validator expects ── */
  const buildChanges = () => {
    const legIndex = {};
    data.groups.forEach((g) => g.events.forEach((e) => e.legs.forEach((l) => { legIndex[l.ruleKey] = l; })));

    return Object.entries(pending).map(([ruleKey, edit]) => {
      const leg = legIndex[ruleKey];
      if (leg?.dimension) {
        // Every declared variant must be sent, including the ones left untouched.
        return {
          ruleKey,
          variants: leg.variants.map((v) => ({
            value: v.value,
            accountId: edit.variants?.[v.value] || v.current?.accountId || null,
          })),
        };
      }
      return { ruleKey, accountId: edit.accountId || leg?.current?.accountId || null };
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await createPostingRuleChange({ changes: buildChanges(), note });
      message.success(res.message || 'Change request submitted for approval');
      setPending({});
      setNote('');
      setSubmitOpen(false);
      changeView('requests');
      fetchRequests();
      fetchAll();
    } catch (err) {
      message.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    try {
      const res = await seedPostingRules();
      message.success(res.message || 'Posting rules seeded');
      fetchAll();
    } catch (err) {
      message.error(apiError(err));
    }
  };

  const handleCancelRequest = (record) => {
    Modal.confirm({
      title: `Withdraw change request ${record.reference}?`,
      icon: <StopOutlined />,
      content: 'The pending approval will be withdrawn. No posting rule has been changed yet.',
      okText: 'Withdraw',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await cancelPostingRuleChange(record._id);
          message.success(`${record.reference} cancelled`);
          fetchRequests();
        } catch (err) {
          message.error(apiError(err));
        }
      },
    });
  };

  const openHistory = async (leg) => {
    try {
      const res = await getPostingRuleHistory(leg.ruleKey);
      setHistoryModal({ leg, ...(res.data || {}) });
    } catch (err) {
      message.error(apiError(err));
    }
  };

  /* ── One leg = one row ── */
  const legColumns = [
    {
      title: '', dataIndex: 'side', width: 60,
      render: (side) => SIDE_TAG[side] || null,
    },
    {
      title: 'Leg', dataIndex: 'label', width: 230,
      render: (label, leg) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{label}</Text>
          <Space size={4} wrap>
            {leg.optional && (
              <Tooltip title="This leg only appears in some entries, depending on the amounts.">
                <Tag style={{ fontSize: 10, marginInlineEnd: 0 }}>optional</Tag>
              </Tooltip>
            )}
            {leg.modeRouting && <Tag color="purple" style={{ fontSize: 10, marginInlineEnd: 0 }}>per payment mode</Tag>}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Amount', dataIndex: 'amountLabel', width: 240,
      render: (amountLabel, leg) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>{amountLabel}</Text>
          {leg.runtimeOverride && (
            <Tooltip title={leg.runtimeOverride}>
              <Text type="warning" style={{ fontSize: 11 }}>
                <InfoCircleOutlined /> the source record can override this
              </Text>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Account', key: 'account',
      render: (_, leg) => {
        if (leg.modeRouting) {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              One line per split, routed by <b>Payment Mode Routing</b> (top of this page).
            </Text>
          );
        }

        if (leg.dimension) {
          return (
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {leg.variants.map((v) => (
                <Row key={v.value} gutter={8} align="middle" wrap={false}>
                  <Col flex="130px">
                    <Text type="secondary" style={{ fontSize: 12 }}>{v.label}</Text>
                  </Col>
                  <Col flex="auto">
                    <Select
                      size="small"
                      showSearch
                      optionFilterProp="label"
                      style={{ width: '100%', minWidth: 220 }}
                      placeholder="Select account"
                      value={pending[leg.ruleKey]?.variants?.[v.value] || v.current?.accountId || undefined}
                      status={pending[leg.ruleKey]?.variants?.[v.value] ? 'warning' : undefined}
                      onChange={(val) => setVariant(leg.ruleKey, v.value, val)}
                      options={accountOptions}
                    />
                  </Col>
                </Row>
              ))}
            </Space>
          );
        }

        return (
          <Select
            size="small"
            showSearch
            optionFilterProp="label"
            style={{ width: '100%', minWidth: 260 }}
            placeholder="Select account"
            value={pending[leg.ruleKey]?.accountId || leg.current?.accountId || undefined}
            status={pending[leg.ruleKey]?.accountId ? 'warning' : undefined}
            onChange={(val) => setDefault(leg.ruleKey, val)}
            options={accountOptions}
          />
        );
      },
    },
    {
      title: 'Currently', key: 'current', width: 220,
      render: (_, leg) => {
        if (leg.modeRouting) return <Text type="secondary">—</Text>;
        if (leg.dimension) {
          return (
            <Space direction="vertical" size={2}>
              {leg.variants.map((v) => (
                <div key={v.value} style={{ fontSize: 12 }}>
                  <AccountBadge current={v.current} />
                </div>
              ))}
            </Space>
          );
        }
        return <AccountBadge current={leg.current} />;
      },
    },
    {
      title: '', key: 'history', width: 44,
      render: (_, leg) => (leg.modeRouting ? null : (
        <Tooltip title="Change history">
          <Button size="small" type="text" icon={<HistoryOutlined />} onClick={() => openHistory(leg)} />
        </Tooltip>
      )),
    },
  ];

  const requestColumns = [
    { title: 'Reference', dataIndex: 'reference', width: 130 },
    { title: 'Rules', dataIndex: 'changeCount', width: 70, align: 'right' },
    { title: 'Reason', dataIndex: 'note', ellipsis: true, render: (n) => n || <Text type="secondary">—</Text> },
    {
      title: 'Changes', key: 'changes', ellipsis: true,
      render: (_, r) => (r.changes || []).map((c) => `${c.eventLabel} → ${c.legLabel}`).join('; ') || '—',
    },
    { title: 'Requested By', dataIndex: ['createdBy', 'name'], width: 140, render: (n) => n || '—' },
    { title: 'Requested On', dataIndex: 'createdAt', width: 140, render: (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—') },
    { title: 'Status', dataIndex: 'status', width: 110, render: (s) => <Tag color={reqStatusColors[s]}>{s}</Tag> },
    {
      title: '', width: 60,
      render: (_, record) => (record.status === 'PENDING' ? (
        <Tooltip title="Withdraw pending request">
          <Button size="small" danger icon={<StopOutlined />} onClick={() => handleCancelRequest(record)} />
        </Tooltip>
      ) : null),
    },
  ];

  if (loading && !data.groups.length) {
    return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;
  }

  const { meta } = data;

  return (
    <div>
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[8, 8]}>
        <Col>
          <Space>
            <Title level={5} style={{ margin: 0 }}>Posting Rules</Title>
            <Segmented
              value={view}
              onChange={changeView}
              options={[
                { label: 'Rules', value: 'rules' },
                { label: 'Change Requests', value: 'requests' },
              ]}
            />
          </Space>
        </Col>
        <Col>
          <Space wrap>
            {pendingCount > 0 && (
              <Tooltip title="Discard all pending edits">
                <Button icon={<UndoOutlined />} onClick={discard}>Discard</Button>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={view === 'rules' ? fetchAll : fetchRequests} />
            </Tooltip>
            <Badge count={pendingCount} offset={[-4, 4]}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                disabled={pendingCount === 0}
                onClick={() => setSubmitOpen(true)}
              >
                Submit for Approval
              </Button>
            </Badge>
          </Space>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      {view === 'rules' && (
        <>
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message="You are choosing WHICH account each automatic entry hits — not how much."
            description="The amounts are calculated by the system and shown here read-only, so an entry can never be left unbalanced. Changes are not live until they are approved; existing journal entries are never rewritten."
          />

          {meta?.needsSeed && (
            <Alert
              style={{ marginBottom: 16 }}
              type="warning"
              showIcon
              message={`${(meta.legCount || 0) - (meta.seededCount || 0)} rule(s) have not been set up yet`}
              description="Seeding fills them in with the account each entry has always used, so nothing changes until you edit something."
              action={<Button size="small" icon={<ThunderboltOutlined />} onClick={handleSeed}>Seed Rules</Button>}
            />
          )}

          {meta?.fallbackCount > 0 && (
            <Alert
              style={{ marginBottom: 16 }}
              type="warning"
              showIcon
              message={`${meta.fallbackCount} rule(s) point at a deleted or disabled account`}
              description="Those entries are falling back to the built-in default account and logging a warning. Pick a live account below to clear this."
            />
          )}

          {meta?.missingCount > 0 && (
            <Alert
              style={{ marginBottom: 16 }}
              type="error"
              showIcon
              message={`${meta.missingCount} rule(s) resolve to no account at all`}
              description="The built-in account is missing from this Chart of Accounts. Run Bootstrap on the Chart of Accounts page, then refresh."
            />
          )}

          {data.groups.length === 0 ? (
            <Empty description="No posting rules found" />
          ) : (
            <Collapse
              defaultActiveKey={['PAYMENTS', 'PURCHASE']}
              items={data.groups.map((group) => ({
                key: group.key,
                label: (
                  <Space direction="vertical" size={0}>
                    <Text strong>{group.label}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{group.description}</Text>
                  </Space>
                ),
                children: (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {group.events.map((event) => (
                      <Card
                        key={event.key}
                        size="small"
                        title={
                          <Space direction="vertical" size={0} style={{ padding: '4px 0' }}>
                            <Text strong>{event.label}</Text>
                            {event.description && (
                              <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'normal' }}>
                                {event.description}
                              </Text>
                            )}
                          </Space>
                        }
                        extra={event.source ? <Tag color="blue">{event.source}</Tag> : null}
                      >
                        <DataTable persistId="accounting.posting-rules.legs" showToolbar={false}
                          dataSource={event.legs.map((l) => ({ ...l, key: l.ruleKey }))}
                          columns={legColumns}
                          pagination={false}
                          size="small"
                          showHeader
                          scroll={{ x: 1000 }}
                        />
                      </Card>
                    ))}
                  </Space>
                ),
              }))}
            />
          )}
        </>
      )}

      {view === 'requests' && (
        <DataTable persistId="accounting.posting-rules.requests"
          dataSource={(requests.data || []).map((r, i) => ({ ...r, key: r._id || i }))}
          columns={requestColumns}
          loading={reqLoading}
          size="small"
          scroll={{ x: 1000 }}
          pagination={{ pageSize: 15, showTotal: (t) => `${t} requests`, showSizeChanger: false }}
          locale={{ emptyText: <Empty description="No posting rule changes yet" /> }}
        />
      )}

      {/* ── Submit modal ── */}
      <Modal
        title="Submit posting rule changes"
        open={submitOpen}
        onOk={handleSubmit}
        onCancel={() => setSubmitOpen(false)}
        confirmLoading={saving}
        okText="Submit for Approval"
        width={640}
      >
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          These {pendingCount} rule{pendingCount === 1 ? '' : 's'} will be re-pointed once approved.
          Journal entries already posted are not touched — the new mapping applies to entries created from then on.
        </Paragraph>
        <Input.TextArea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why is this changing? The approver sees this."
        />
      </Modal>

      {/* ── History modal ── */}
      <Modal
        title={historyModal ? `History — ${historyModal.leg?.label}` : 'History'}
        open={!!historyModal}
        onCancel={() => setHistoryModal(null)}
        footer={null}
        width={640}
      >
        {historyModal && (
          (historyModal.history || []).length === 0 ? (
            <Empty description="This rule has never been changed" />
          ) : (
            <DataTable persistId="accounting.posting-rules.history" showToolbar={false}
              dataSource={(historyModal.history || []).map((h, i) => ({ ...h, key: i }))}
              columns={[
                { title: 'Version', dataIndex: 'version', width: 80 },
                {
                  title: 'Pointed at', key: 'acct', render: (_, h) => (
                    h.variants?.length
                      ? h.variants.map((v) => `${v.value}: ${v.accountCode || '—'}`).join(', ')
                      : `${h.accountCode || '—'} ${h.accountName || ''}`
                  ),
                },
                { title: 'Changed By', dataIndex: ['changedBy', 'name'], width: 130, render: (n) => n || '—' },
                { title: 'When', dataIndex: 'changedAt', width: 150, render: (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—') },
              ]}
              pagination={false}
              size="small"
            />
          )
        )}
      </Modal>
    </div>
  );
}
